// Shared utilities + interactive postcode lead form used across all variants.
// Each variant wraps <LeadForm> with its own visual chrome.
//
// ES-module conversion notes (2026-04-26): originally loaded via
// Babel-standalone in Website Design-Varianten.html with React on global
// scope and `Object.assign(window, ...)` exports. Now compiled by Vite
// for the Vue project — explicit React import + named exports.

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { router } from '@inertiajs/core';
import { trackEvent } from '@/lib/tracking';

// Read the active design-variant slug from the current URL. Used to
// propagate the variant across navigation so a user previewing
// ?variant=v05_technical on / lands on /förderung-finder?variant=v05_technical
// instead of falling out into the default Vue chrome on the next click.
function currentVariantSlug() {
  if (typeof window === 'undefined') return null;
  try {
    const u = new URL(window.location.href);
    return u.searchParams.get('variant') || u.searchParams.get('utm_content') || null;
  } catch {
    return null;
  }
}

// Append `?variant=<slug>` to an internal href if the current URL has
// one and the destination doesn't already specify it. Leaves external,
// mailto:, tel:, and hash-only hrefs untouched.
function withVariant(href) {
  if (!href || typeof href !== 'string') return href;
  if (/^(?:[a-z]+:|#|mailto:|tel:)/i.test(href)) return href;
  const slug = currentVariantSlug();
  if (!slug) return href;
  if (/[?&]variant=/.test(href)) return href;
  return href + (href.includes('?') ? '&' : '?') + 'variant=' + encodeURIComponent(slug);
}

// Inertia <Link> shim. The portal is a Vue + Inertia app and the React
// variants are mounted via veaury — there's no @inertiajs/react package
// to import a real <Link> from. This shim renders an <a> and intercepts
// the click to call the framework-agnostic core router, giving us SPA
// navigation without a full reload. It also auto-propagates the active
// ?variant= so the design context survives clicking through the portal.
const Link = React.forwardRef(function Link(
  { href, children, onClick, target, rel, ...rest },
  ref,
) {
  const finalHref = withVariant(href);
  const handle = (e) => {
    if (onClick) onClick(e);
    if (e.defaultPrevented) return;
    if (target && target !== '_self') return;
    if (e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (!finalHref || /^(?:[a-z]+:|#|mailto:|tel:)/i.test(finalHref)) return;
    e.preventDefault();
    router.visit(finalHref);
  };
  return (
    <a ref={ref} href={finalHref} onClick={handle} target={target} rel={rel} {...rest}>
      {children}
    </a>
  );
});

// ────────────────────────────────────────────────────────────
// Striped photography placeholder — never SVG-illustrate people/products.
// ────────────────────────────────────────────────────────────
// Per-tone fallback Unsplash photos so v01–v14 ship with real imagery
// instead of striped placeholders. `src` overrides the default; the
// caller's `label` becomes the alt text.
// Unsplash defaults: w=400 + q=70 (down from w=800/q=80). PSI flagged
// these being served at 533x532 natural while displayed at 126x84 in
// testimonial/avatar slots - 62 KB per slot wasted. 400w + q=70 is
// indistinguishable at any size the variants actually use, while
// halving the byte weight portfolio-wide.
const PHOTO_DEFAULTS = {
  warm:  'https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=400&q=70',
  cool:  'https://images.unsplash.com/photo-1545209463-e2825498edbf?auto=format&fit=crop&w=400&q=70',
  sage:  'https://images.unsplash.com/photo-1559827260-dc66d52bef19?auto=format&fit=crop&w=400&q=70',
  sand:  'https://images.unsplash.com/photo-1497440001374-f26997328c1b?auto=format&fit=crop&w=400&q=70',
  ink:   'https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?auto=format&fit=crop&w=400&q=70',
  sky:   'https://images.unsplash.com/photo-1466611653911-95081537e5b7?auto=format&fit=crop&w=400&q=70',
  cream: 'https://images.unsplash.com/photo-1605980776566-0486c3ac7617?auto=format&fit=crop&w=400&q=70',
};

function PhotoPH({ label, ratio = '4 / 3', tone = 'warm', radius = 0, style = {}, src }) {
  const photo = src || PHOTO_DEFAULTS[tone] || PHOTO_DEFAULTS.warm;
  return (
    <div style={{
      aspectRatio: ratio,
      width: '100%',
      borderRadius: radius,
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: '#e9e4d8',
      ...style,
    }}>
      <img
        src={photo}
        alt={label || ''}
        loading="lazy"
        style={{
          position: 'absolute', inset: 0,
          width: '100%', height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// LeadForm — multi-step interactive form. Postcode → property type →
// confirmation. All variants render this inside their own shell.
// `theme` controls colors; `compact` shrinks padding for dense layouts.
// ────────────────────────────────────────────────────────────
function LeadForm({
  theme = {},
  service = 'kombi', // pv | wp | kombi
  compact = false,
  ctaLabel = 'Kostenlos vergleichen',
  showSavings = true,
  variant = 'card', // card | inline | dark
}) {
  const t = {
    bg: theme.bg || '#ffffff',
    fg: theme.fg || '#111111',
    muted: theme.muted || '#6b6b6b',
    border: theme.border || '#e5e5e5',
    accent: theme.accent || '#0f6b3a',
    accentFg: theme.accentFg || '#ffffff',
    inputBg: theme.inputBg || '#ffffff',
    radius: theme.radius ?? 8,
    font: theme.font || 'inherit',
  };
  const [step, setStep] = useState(0);
  const [zip, setZip] = useState('');
  const [zipErr, setZipErr] = useState('');
  const [property, setProperty] = useState('');
  const [roof, setRoof] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // UWG § 7 separate phone-advertising opt-in. UNticked by default; governs the
  // right to call/SMS. Submission is not blocked by it.
  const [phoneConsent, setPhoneConsent] = useState(false);
  const [done, setDone] = useState(false);

  const serviceLabel = { pv: 'Photovoltaik', wp: 'Wärmepumpe', kombi: 'PV + Wärmepumpe' }[service];

  const submitZip = (e) => {
    e?.preventDefault?.();
    if (!/^\d{5}$/.test(zip)) { setZipErr('Bitte 5-stellige PLZ eingeben'); return; }
    setZipErr('');
    setStep(1);
    trackEvent('funnel_step_next', { event_category: 'funnel', value: 1, metadata: { funnel: 'react_variant', step_key: 'location' } });
  };
  const submitProperty = (e) => {
    e?.preventDefault?.();
    if (!property) return;
    setStep(2);
    trackEvent('funnel_step_next', { event_category: 'funnel', value: 2, metadata: { funnel: 'react_variant', step_key: 'property' } });
  };
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState('');
  const submitContact = (e) => {
    e?.preventDefault?.();
    // Phone-only final step (2026-05-30): phone is the single required field,
    // matching the Vue funnel. Name + email are optional; an empty name is
    // enriched post-submit on the Success page.
    if (!phone) return;
    const trimmed = name.trim();
    const firstName = trimmed ? trimmed.split(/\s+/)[0] : null;
    const lastName = trimmed ? (trimmed.split(/\s+/).slice(1).join(' ') || null) : null;
    const propertyType = property === 'Mehrfamilienhaus' ? 'wohnung' : 'haus';
    setSubmitting(true);
    setSubmitErr('');
    router.post('/leads', {
      postal_code: zip,
      service_type: service,
      property_type: propertyType,
      first_name: firstName,
      last_name: lastName,
      email: email || null,
      phone,
      privacy_accepted: true,
      // UWG § 7 separate opt-in: only when ticked does the server record
      // phone/sms call-consent (the React variants POST straight to /leads).
      phone_advertising_consent: phoneConsent,
      // Button-as-consent for the legacy privacy notice. The separate UWG sec 7
      // phone-advertising opt-in is added in the consent rework.
      consent_acknowledged: true,
    }, {
      preserveScroll: true,
      // lead_submit_success is fired by Success.vue (the post-navigation page),
      // NOT here - firing in both places is the tracking double-fire we removed.
      onSuccess: () => { setDone(true); },
      onError: (errors) => {
        const first = Object.values(errors || {})[0];
        setSubmitErr(typeof first === 'string' ? first : 'Übermittlung fehlgeschlagen. Bitte erneut versuchen.');
      },
      onFinish: () => setSubmitting(false),
    });
  };

  // funnel_view: fire once when the form enters the viewport. The React variants
  // previously fired NO funnel_view, so inline exposure was invisible to the
  // dashboard (the 0.12% undercount). IntersectionObserver so a below-fold form
  // only counts when actually seen.
  const rootRef = useRef(null);
  const viewFired = useRef(false);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((en) => en.isIntersecting) && !viewFired.current) {
        viewFired.current = true;
        trackEvent('funnel_view', { event_category: 'funnel', metadata: { funnel: 'react_variant', service } });
        io.disconnect();
      }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [service]);
  const reset = () => { setStep(0); setZip(''); setProperty(''); setRoof(''); setName(''); setEmail(''); setPhone(''); setDone(false); };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    padding: compact ? '11px 12px' : '14px 14px',
    fontSize: compact ? 14 : 15,
    fontFamily: t.font,
    background: t.inputBg,
    color: t.fg,
    border: `1px solid ${t.border}`,
    borderRadius: t.radius,
    outline: 'none',
  };
  const btnStyle = {
    padding: compact ? '11px 16px' : '14px 18px',
    fontSize: compact ? 14 : 15,
    fontFamily: t.font,
    fontWeight: 600,
    background: t.accent,
    color: t.accentFg,
    border: 'none',
    borderRadius: t.radius,
    cursor: 'pointer',
    letterSpacing: '0.01em',
  };
  const tile = (active) => ({
    flex: 1, minWidth: 0,
    padding: compact ? '12px 10px' : '14px 12px',
    border: `1.5px solid ${active ? t.accent : t.border}`,
    background: active ? `color-mix(in oklab, ${t.accent} 8%, ${t.bg})` : t.bg,
    color: t.fg,
    borderRadius: t.radius,
    cursor: 'pointer',
    fontFamily: t.font,
    fontSize: compact ? 13 : 14,
    fontWeight: 500,
    textAlign: 'left',
    display: 'flex', flexDirection: 'column', gap: 2,
    transition: 'border-color .15s, background .15s',
  });

  if (done) {
    return (
      <div style={{ fontFamily: t.font, color: t.fg, padding: compact ? 16 : 22, textAlign: 'center' }}>
        <div style={{
          width: 44, height: 44, margin: '0 auto 14px',
          borderRadius: 999, background: t.accent, color: t.accentFg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <CheckIcon size={22} color={t.accentFg} strokeWidth={3} />
        </div>
        <div style={{ fontSize: compact ? 16 : 18, fontWeight: 600, marginBottom: 6 }}>Danke, {name.split(' ')[0]}!</div>
        <div style={{ fontSize: compact ? 13 : 14, color: t.muted, lineHeight: 1.5 }}>
          Wir vergleichen jetzt regionale Fachbetriebe in <strong style={{color:t.fg}}>{zip}</strong> und melden uns innerhalb von 24 Stunden.
        </div>
        <button onClick={reset} style={{
          marginTop: 14, background: 'transparent', border: 'none',
          color: t.muted, fontSize: 12, cursor: 'pointer', textDecoration: 'underline',
          fontFamily: t.font,
        }}>Neue Anfrage</button>
      </div>
    );
  }

  return (
    <div ref={rootRef} style={{ fontFamily: t.font, color: t.fg }}>
      {/* progress */}
      <div style={{ display: 'flex', gap: 4, marginBottom: compact ? 10 : 14 }}>
        {[0,1,2].map(i => (
          <div key={i} style={{
            flex: 1, height: 3, borderRadius: 2,
            background: i <= step ? t.accent : t.border,
            transition: 'background .25s',
          }}/>
        ))}
      </div>

      {step === 0 && (
        <form onSubmit={submitZip}>
          <label style={{ display: 'block', fontSize: 12, color: t.muted, marginBottom: 6, letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 600 }}>
            Schritt 1 · Postleitzahl
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              inputMode="numeric"
              maxLength={5}
              placeholder="z. B. 80331"
              value={zip}
              onChange={e => { setZip(e.target.value.replace(/\D/g,'').slice(0,5)); setZipErr(''); }}
              style={{ ...inputStyle, flex: 1 }}
            />
            <button type="submit" style={btnStyle}>{ctaLabel}</button>
          </div>
          {zipErr && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 6 }}>{zipErr}</div>}
          {showSavings && (
            <div style={{ display: 'flex', gap: compact ? 12 : 18, marginTop: compact ? 12 : 16, fontSize: 12, color: t.muted }}>
              <Tick t={t}>100 % kostenlos</Tick>
              <Tick t={t}>Geprüfte Betriebe</Tick>
              <Tick t={t}>In 60 Sekunden</Tick>
            </div>
          )}
        </form>
      )}

      {step === 1 && (
        <form onSubmit={submitProperty}>
          <label style={{ display: 'block', fontSize: 12, color: t.muted, marginBottom: 8, letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 600 }}>
            Schritt 2 · Ihr Gebäude
          </label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            {[
              ['Einfamilienhaus','Eigentum'],
              ['Mehrfamilienhaus','2–6 Parteien'],
              ['Doppelhaus','Reihenhaus'],
            ].map(([a,b]) => (
              <button type="button" key={a} onClick={() => setProperty(a)} style={tile(property===a)}>
                <span style={{ fontWeight: 600 }}>{a}</span>
                <span style={{ fontSize: 11, color: t.muted }}>{b}</span>
              </button>
            ))}
          </div>
          {service !== 'wp' && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {['Satteldach','Flachdach','Walmdach','Pultdach'].map(r => (
                <button type="button" key={r} onClick={() => setRoof(r)} style={{
                  ...tile(roof===r),
                  textAlign: 'center', padding: '9px 6px',
                }}>
                  <span style={{ fontWeight: 500, fontSize: 12 }}>{r}</span>
                </button>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => setStep(0)} style={{
              ...btnStyle, background: 'transparent', color: t.muted,
              border: `1px solid ${t.border}`, flex: '0 0 auto',
            }}>Zurück</button>
            <button type="submit" disabled={!property} style={{
              ...btnStyle, flex: 1,
              opacity: property ? 1 : 0.4, cursor: property ? 'pointer' : 'not-allowed',
            }}>Weiter zu Schritt 3</button>
          </div>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={submitContact}>
          <label style={{ display: 'block', fontSize: 12, color: t.muted, marginBottom: 8, letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 600 }}>
            Schritt 3 · Ihre Kontaktdaten
          </label>
          <div style={{ fontSize: 13, color: t.fg, fontWeight: 600, marginBottom: 10 }}>
            Ihre passenden Fachbetriebe stehen bereit — Telefonnummer eingeben, wir melden uns mit Ihren besten Angeboten.
          </div>
          <div style={{ display: 'grid', gap: 8, marginBottom: 10 }}>
            <input placeholder="Telefon" inputMode="tel" value={phone} onChange={e=>setPhone(e.target.value)} style={inputStyle}/>
            <input placeholder="Vor- und Nachname (optional)" value={name} onChange={e=>setName(e.target.value)} style={inputStyle}/>
            <input placeholder="E-Mail (optional)" type="email" value={email} onChange={e=>setEmail(e.target.value)} style={inputStyle}/>
          </div>
          {/* UWG § 7 SEPARATE, UNticked phone-advertising opt-in. Submission is
              not blocked by it; it only governs the right to call/SMS. LEGAL:
              wording mirrors config('consent.phone_optin_checkbox'); Fachanwalt
              sign-off required before go-live. */}
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={phoneConsent} onChange={e=>setPhoneConsent(e.target.checked)} style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0, accentColor: t.accent }}/>
            <span style={{ fontSize: 11, lineHeight: 1.45, color: t.muted }}>
              Ja, der jeweils vermittelte Fachbetrieb darf mich unter der angegebenen Rufnummer telefonisch und per SMS zu meinem kostenlosen Angebot kontaktieren. Freiwillig, jederzeit formfrei widerrufbar.
            </span>
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => setStep(1)} style={{
              ...btnStyle, background: 'transparent', color: t.muted,
              border: `1px solid ${t.border}`, flex: '0 0 auto',
            }}>Zurück</button>
            <button type="submit" disabled={!phone || submitting} style={{
              ...btnStyle, flex: 1,
              opacity: (phone && !submitting) ? 1 : 0.4,
              cursor: (phone && !submitting) ? 'pointer' : 'not-allowed',
            }}>{submitting ? 'Wird gesendet…' : 'Kostenlos vergleichen →'}</button>
          </div>
          {submitErr && (
            <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 8 }}>{submitErr}</div>
          )}
          <div style={{ fontSize: 11, color: t.muted, marginTop: 10, lineHeight: 1.5 }}>
            Mit Klick auf „Vergleichen" akzeptieren Sie unsere <Link href="/datenschutz" style={{color:'inherit'}}>Datenschutzerklärung</Link>. Keine versteckten Kosten.
          </div>
        </form>
      )}
    </div>
  );
}

// Stroked checkmark SVG — replaces the Unicode ✓ glyph (which renders
// inconsistently across font families and flags as AI-slop).
function CheckIcon({ size = 14, color = 'currentColor', strokeWidth = 2.4 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// Stroked star — for review ratings. Use `filled` for the lit segment.
function StarIcon({ size = 16, filled = true, color = 'currentColor' }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? color : 'none'}
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

// Accessible star rating — replaces `'★'.repeat(n)` text patterns. Renders
// SVG stars with a single aria-label that announces the score numerically.
// `value` may be fractional; integer floor lights up that many stars.
function StarRating({ value = 5, max = 5, size = 16, color = 'currentColor', gap = 2, label }) {
  const lit = Math.max(0, Math.min(max, Math.floor(value)));
  const aria = label || `${value.toString().replace('.', ',')} von ${max} Sternen`;
  return (
    <span
      role="img"
      aria-label={aria}
      style={{ display: 'inline-flex', alignItems: 'center', gap, color, lineHeight: 0 }}
    >
      {Array.from({ length: max }, (_, i) => (
        <StarIcon key={i} size={size} filled={i < lit} color={color} />
      ))}
    </span>
  );
}

function Tick({ t, children }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        width: 16, height: 16, borderRadius: 999,
        background: t.accent, color: t.accentFg,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <CheckIcon size={10} color={t.accentFg} strokeWidth={3} />
      </span>
      {children}
    </span>
  );
}

// ────────────────────────────────────────────────────────────
// NestedCard — Doppelrand (double-bezel) card primitive.
// Outer shell hairline + inner core with subtle inset highlight; concentric
// border-radius for hardware-precise feel. Use as a container for premium
// hero forms, testimonials, feature cards, comparison rows.
// ────────────────────────────────────────────────────────────
function NestedCard({
  children,
  outerRadius = 28,
  inset = 6,
  background = '#ffffff',
  shellTone = 'rgba(15, 23, 42, 0.04)',
  hairline = 'rgba(15, 23, 42, 0.06)',
  insetHighlight = 'rgba(255, 255, 255, 0.15)',
  style = {},
  innerStyle = {},
  ...rest
}) {
  return (
    <div
      style={{
        background: shellTone,
        boxShadow: `inset 0 0 0 1px ${hairline}`,
        borderRadius: outerRadius,
        padding: inset,
        ...style,
      }}
      {...rest}
    >
      <div
        style={{
          background,
          borderRadius: Math.max(0, outerRadius - inset),
          boxShadow: `inset 0 1px 1px ${insetHighlight}, 0 1px 2px rgba(15, 23, 42, 0.05), 0 1px 3px rgba(15, 23, 42, 0.04)`,
          ...innerStyle,
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Common nav bar — slim, varies by theme
// ────────────────────────────────────────────────────────────
// Default nav: real routes, real labels. Each item is {label, href}.
const DEFAULT_NAV_LINKS = [
  { label: 'Ratgeber', href: '/ratgeber' },
  { label: 'Förderung', href: '/förderung-finder' },
  { label: 'Tools', href: '/tools-wissen' },
  { label: 'Kontakt', href: '/kontakt' },
];

function NavBar({ brand, links = DEFAULT_NAV_LINKS, theme = {}, accent = false, ctaLabel = 'Angebote vergleichen', ctaHref = '/angebot-vergleichen' }) {
  const t = {
    bg: theme.bg || 'transparent',
    fg: theme.fg || '#111',
    muted: theme.muted || '#6b6b6b',
    border: theme.border || 'transparent',
    accent: theme.accent || '#0f6b3a',
    accentFg: theme.accentFg || '#fff',
    font: theme.font || 'inherit',
  };
  // Accept either ['Label', ...] (legacy) or [{label, href}, ...]. Legacy
  // strings get mapped to the default link table when possible.
  const LEGACY_HREF = {
    'Ratgeber': '/ratgeber',
    'Kosten': '/ratgeber',
    'Förderung': '/förderung-finder',
    'Tools': '/tools-wissen',
    'Kontakt': '/kontakt',
    'Über uns': '/über-uns',
    'Partner': '/partner',
    'Forum': '/forum',
    'Glossar': '/glossar',
    'Marktanalyse': '/marktanalyse',
    'Produkte': '/produkte',
    'Vorteile': '/vorteile',
    'So funktioniert es': '/so-funktioniert-es',
  };
  const items = links.map(l => typeof l === 'string'
    ? { label: l, href: LEGACY_HREF[l] || '/seiten' }
    : l);
  return (
    <nav style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 32px', borderBottom: `1px solid ${t.border}`, background: t.bg,
      fontFamily: t.font, color: t.fg,
    }}>
      <Link href="/" style={{ fontWeight: 700, fontSize: 16, letterSpacing: '-0.01em', color: 'inherit', textDecoration: 'none' }}>{brand}</Link>
      <div style={{ display: 'flex', gap: 22, fontSize: 13, color: t.muted }}>
        {items.map(l => (
          <Link key={l.label} href={l.href} style={{ color: 'inherit', textDecoration: 'none' }}>{l.label}</Link>
        ))}
      </div>
      <Link href={ctaHref} style={{
        padding: '8px 14px', fontSize: 13, fontWeight: 600,
        background: accent ? t.accent : 'transparent',
        color: accent ? t.accentFg : t.fg,
        border: accent ? 'none' : `1px solid ${t.border === 'transparent' ? t.fg : t.border}`,
        borderRadius: 6, cursor: 'pointer', fontFamily: t.font,
        textDecoration: 'none', display: 'inline-block',
      }}>{ctaLabel}</Link>
    </nav>
  );
}

// Trust pill row
function TrustRow({ items, theme = {}, style = {} }) {
  const t = { fg: theme.fg || '#111', muted: theme.muted || '#666', font: theme.font || 'inherit' };
  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontFamily: t.font, fontSize: 12, color: t.muted, ...style }}>
      {items.map(i => (
        <span key={i.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <strong style={{ color: t.fg, fontSize: 14 }}>{i.value}</strong> {i.label}
        </span>
      ))}
    </div>
  );
}

export {
  LeadForm,
  PhotoPH,
  NavBar,
  TrustRow,
  Tick,
  CheckIcon,
  StarIcon,
  StarRating,
  NestedCard,
  DEFAULT_NAV_LINKS,
  Link,
  router,
};
